import Image from "next/image";

import { FcPositiveDynamic } from "react-icons/fc";
import { TbChartCovariate, TbChartScatter } from "react-icons/tb";

import { twMerge } from "tailwind-merge";

import { faBitcoin } from "@fortawesome/free-brands-svg-icons/faBitcoin";
import {
  faAtom,
  faCoins,
  faCubes,
  faLandmark,
  faLayerGroup,
  faListAlt,
  faNewspaper,
  faRobot,
  faSatelliteDish,
  faScroll,
  faSearchPlus,
  faUniversity,
  faUserTie,
} from "@fortawesome/free-solid-svg-icons";
import { faChartArea } from "@fortawesome/free-solid-svg-icons/faChartArea";
import { faChartBar } from "@fortawesome/free-solid-svg-icons/faChartBar";
import { faChartLine } from "@fortawesome/free-solid-svg-icons/faChartLine";
import { faCompass } from "@fortawesome/free-solid-svg-icons/faCompass";
import { faDollarSign } from "@fortawesome/free-solid-svg-icons/faDollarSign";
import { faExchangeAlt } from "@fortawesome/free-solid-svg-icons/faExchangeAlt";
import { faInfo } from "@fortawesome/free-solid-svg-icons/faInfo";
import { faUniversalAccess } from "@fortawesome/free-solid-svg-icons/faUniversalAccess";

import FontAwesomeIcon from "components/FontAwesomeIcon";
import {
  TOOL_Alt_DATA_ANALYSIS_DESCRIPTION,
  TOOL_Alt_DATA_ANALYSIS_NAME,
} from "components/ToolAltDataAnalysis";
import {
  TOOL_DESCRIPTION as TOOL_KRVALUE_DESCRIPTION,
  TOOL_NAME as TOOL_KRVALUE_NAME,
} from "components/ToolKoreaValueUp/constants";
import {
  TOOL_PRECIOUS_METALS_DESCRIPTION,
  TOOL_PRECIOUS_METALS_NAME,
} from "components/ToolPreciousMetal";
import {
  TOOL_SHAREHOLDER_REGISTRY_DESCRIPTION,
  TOOL_SHAREHOLDER_REGISTRY_NAME,
} from "components/ToolShareholderRegistry";
import { emberAppBaseUrl } from "utils/envVariables";

const comparablesIcon = require("../assets/comparables-white.svg");

const allUsers = [
  "isIndividualPass",
  "isInsightProvider",
  "isCorporateOfficer",
  "isIntroducer",
  "isPreviewPass",
  "isClient",
];

export const marketBg = "bg-gradient-to-br from-[#1d4ed8] to-[#06b6d4]";
export const screenerBg = "bg-gradient-to-br from-[#24a9a7] to-[#4db364]";
export const eventsBg = "bg-jade bg-gradient-to-br from-[#7c3aed] to-[#c026d3]";
export const chartingBg =
  "bg-jade bg-gradient-to-br from-[#0f766e] to-[#0891b2]";
export const commoditiesBg = "bg-gradient-to-br from-[#0d9488] to-[#65a30d]";
export const insightProviderBg = "bg-utah-sky";

export const tools = [
  // Markets
  {
    id: "market-overview",
    title: "Market Overview",
    icon: <FontAwesomeIcon icon={faExchangeAlt} style={{ rotate: "90deg" }} />,
    className: twMerge(marketBg, "group-hover:bg-[#339b77]"),
    description:
      "Global markets performance, gainers and losers and key events",
    link: `/tools/market-overview`,
    type: "markets",
    access: [...allUsers],
    public: true,
  },
  {
    id: "fx-rates",
    title: "FX & Rates",
    icon: <FontAwesomeIcon icon={faDollarSign} />,
    className: twMerge(marketBg, "group-hover:bg-[#339b77]"),
    description: "Real-time FX quotes, forex heatmap and screener",
    link: `/tools/fx-rates`,
    type: "markets",
    access: [...allUsers],
    public: true,
  },
  {
    id: "crypto",
    title: "Crypto",
    icon: <FontAwesomeIcon icon={faBitcoin} />,
    className: twMerge(marketBg, "group-hover:bg-[#339b77]"),
    description:
      "Crypto asset pricing, fundamental data, heatmap, screener and key news",
    link: `/tools/crypto`,
    type: "markets",
    access: [...allUsers],
    public: true,
  },
  {
    id: "heteronomics-inflation-forecasts",
    title: "Heteronomics Inflation Forecasts",
    icon: <FontAwesomeIcon icon={faInfo} />,
    className: twMerge(marketBg, "group-hover:bg-[#339b77]"),
    description: "EU/UK forecasts and proprietary indicators from Heteronomics",
    link: "/tools/heteronomics-inflation-forecasts",
    deniedLink: "/profiles/phil-rush/premium-subscriptions",
    needPremiumAccess: true,
    interfaceSettingKey: "iftEnabled",
    target: "_self",
    enabled: true,
    hideOnRestricted: true,
    access: [
      ...allUsers.filter(
        (user) => !["isPreviewPass", "isCorporateOfficer"].includes(user)
      ),
    ],
    paidOnly: true,
    type: "markets",
    activityKey: "tool.view",
  },
  {
    id: "news-quicktakes",
    title: "News & Quicktakes",
    icon: <FontAwesomeIcon icon={faNewspaper} />,
    className: twMerge(marketBg, "group-hover:bg-[#339b77]"),
    description:
      "Real-time financial news and AI-powered analyst quick takes, powered by Smartkarma 𝛼SK",
    link: `/tools/news-quicktakes`,
    enabled: true,
    access: [...allUsers],
    isNew: true,
    public: true,
    type: "markets",
  },
  {
    id: "smartscripts",
    title: "Smartscripts",
    icon: <FontAwesomeIcon icon={faScroll} />,
    className: twMerge(marketBg, "group-hover:bg-[#339b77]"),
    description: "AI-Enabled Synthetic Expert Call Transcripts",
    link: `/tools/smartscripts`,
    enabled: true,
    access: ["canRequestAIPrimer"],
    isNew: true,
    paidOnly: true,
    type: "markets",
  },

  //charting
  {
    id: "grt",
    title: "Graph Ratio (GRT)",
    icon: <TbChartCovariate />,
    className: twMerge(chartingBg, "group-hover:bg-[#339b77]"),
    description: "Compare and analyse security price ratios",
    href: `/tools/grt-comparison`,
    type: "charting",
    access: [...allUsers],
  },
  {
    id: "chart-builder",
    title: "Chart Builder",
    icon: <FontAwesomeIcon icon={faChartLine} />,
    className: twMerge(chartingBg, "group-hover:bg-[#0266a5]"),
    description:
      "Create simple, beautiful charts that make your Insights stand out",
    href: "http://chartbuilder.smartkarma.com/",
    enabled: true,
    type: "charting",
    rel: "nofollow noreferrer",
    access: [...allUsers],
    activityKey: "tool.view",
  },

  // Event-Driven
  {
    id: "active-situations-monitor",
    title: "Active Situations Monitor",
    icon: <FontAwesomeIcon icon={faSatelliteDish} />,
    className: twMerge(eventsBg, "group-hover:bg-[#339b77]"),
    description:
      "Track active event-driven, index rebalance and ECM situations",
    link: `/tools/active-situations-monitor`,
    type: "event-driven",
    access: [
      ...allUsers.filter((user) => !["isCorporateOfficer"].includes(user)),
    ],
    hideOnRestricted: true,
    public: true,
  },
  {
    id: "quiddity-northbound-monitor",
    title: "Quiddity Northbound Monitor",
    icon: (
      <Image
        src="https://assets.smartkarma.com/tools/tool-n.svg"
        alt="Quiddity Northbound Monitor"
        width={20}
        height={20}
      />
    ),
    className: twMerge(eventsBg, "group-hover:bg-[#339b77]"),
    description: "Monitor China/HK Northbound Liquidity flows",
    link: "/tools/quiddity/north-flow-monitor",
    permission: "canViewQuiddityTools",
    access: ["canViewQuiddityTools"],
    paidOnly: true,
    hideOnRestricted: true,
    type: "event-driven",
  },
  {
    id: "quiddity-southbound-monitor",
    title: "Quiddity Southbound Monitor",
    icon: (
      <Image
        src="https://assets.smartkarma.com/tools/tool-s.svg"
        alt="Quiddity Southbound Monitor"
        width={20}
        height={20}
      />
    ),
    className: twMerge(eventsBg, "group-hover:bg-[#339b77]"),
    description: "Monitor China/HK Southbound Liquidity flows",
    link: "/tools/quiddity/south-flow-monitor",
    access: ["canViewQuiddityTools"],
    hideOnRestricted: true,
    paidOnly: true,
    type: "event-driven",
  },
  {
    id: "quiddity-a-h-shares-monitor",
    title: "Quiddity A/H Shares Monitor",
    icon: (
      <Image
        src="https://assets.smartkarma.com/tools/tool-ah.svg"
        alt="Quiddity A/H Shares Monitor"
        width={20}
        height={20}
      />
    ),
    className: twMerge(eventsBg, "group-hover:bg-[#339b77]"),
    description: "Track China/HK A/H share Premia and Performance by Sector",
    link: "/tools/quiddity/ah-shares-monitor",
    access: ["canViewQuiddityTools"],
    hideOnRestricted: true,
    paidOnly: true,
    type: "event-driven",
  },

  // Screeners
  {
    id: "japan-governance-tool",
    title: "Japan Governance Tool",
    icon: <FontAwesomeIcon icon={faLandmark} />,
    className: twMerge(screenerBg, "group-hover:bg-[#339b77]"),
    description:
      "Monitor Market-wide activism, ownership, governance and earnings signals.",
    link: `/tools/japan-governance-tool`,
    type: "screeners",
    access: [...allUsers],
    public: true,
    isNew: true,
    sortRank: -1,
  },
  {
    id: "smartkarma-smartscore-screener",
    title: "SmartScore Screener",
    icon: (
      <Image
        src="https://assets.smartkarma.com/tools/filter.svg"
        alt="SmartScore Screener"
        width={20}
        height={20}
      />
    ),
    className: twMerge(screenerBg, "group-hover:bg-[#339b77]"),
    breadcrumbSvg: "/filter-black.svg",
    description: "Screen for companies on SmartScore Factors",
    link: `/tools/smart-score-screener`,
    permission: "id",
    access: [...allUsers],
    type: "screeners",
  },
  {
    id: "executives-analytics",
    title: "Executives Analytics",
    icon: (
      <Image
        src={require("../assets/user-chart-white.svg")}
        alt="Executives Analytics"
        width={20}
        height={20}
      />
    ),
    className: twMerge(screenerBg, "group-hover:bg-[#339b77]"),
    breadcrumbSvg: "/filter-black.svg",
    description:
      "Screen for top executives based on the performance of their trades done on their companies.",
    link: `/tools/executives-analytics`,
    access: [...allUsers],
    public: true,
    type: "screeners",
  },

  {
    id: "relative-value",
    title: "Relative Value",
    icon: <FontAwesomeIcon icon={faChartArea} />,
    className: twMerge(screenerBg, "group-hover:bg-[#339b77]"),
    breadcrumbSvg: "/filter-black.svg",
    description:
      "Monitor price ratios of closely correlated pairs such as HoldCos, and Share classes",
    link: `/tools/relative-value`,
    permission: "canViewExecutivesAnalytics",
    access: [...allUsers],
    public: true,
    type: "screeners",
  },
  {
    id: "discovery",
    title: "Discovery",
    icon: <FontAwesomeIcon icon={faCompass} />,
    className: twMerge(screenerBg, "group-hover:bg-[#339b77]"),
    description:
      "Most searched Entities with fewest number of Insights published recently",
    link: `/tools/discovery`,
    enabled: true,
    access: [...allUsers.filter((user) => user !== "isCorporateOfficer")],
    type: "screeners",
    hideOnRestricted: true,
  },
  {
    id: "peers-comparison",
    title: "Peers Comparison",
    icon: <FontAwesomeIcon icon={faChartBar} />,
    className: twMerge(screenerBg, "group-hover:bg-[#339b77]"),
    description: "Compare fundamentals of Peer Companies",
    link: `/tools/peers-comparison`,
    access: [...allUsers.filter((user) => user !== "isPreviewPass")],
    paidOnly: true,
    type: "screeners",
  },
  {
    id: "taiwan-monthly-screener",
    title: "Taiwan Monthly Revenue Monitor",
    icon: (
      <FcPositiveDynamic
        className="[&>g]:!fill-white"
        size={20}
        stroke="white"
        fill="white"
      />
    ),
    className: twMerge(screenerBg, "group-hover:bg-[#339b77]"),
    description:
      "Track monthly revenue trends of Taiwanese listed companies with Smartkarma's Actionability Score",
    link: `/tools/taiwan-monthly-screener`,
    type: "screeners",
    access: [
      ...allUsers.filter(
        (user) => !["isCorporateOfficer", "isPreviewPass"].includes(user)
      ),
    ],
    hideOnRestricted: true,
    permission: "id",
    paidOnly: true,
  },
  {
    id: "fund-screener",
    title: "Fund Screener",
    icon: <FontAwesomeIcon icon={faListAlt} />,
    className: twMerge(screenerBg, "group-hover:bg-[#339b77]"),
    description: "Screen for Funds & Fund Managers",
    link: `/tools/fund-screener`,
    isInternalOnly: false,
    paidOnly: true,
    access: [...allUsers.filter((user) => user !== "isPreviewPass")],
    withBrowserNavigation: true,
    type: "screeners",
  },
  {
    id: "smartkarma-model-portofolios",
    title: "Model Portfolios",
    icon: (
      <Image
        src={require("../assets/stock-exchange.png")}
        alt="Model Portfolios"
        width={32}
        height={32}
      />
    ),
    className: twMerge(screenerBg, "group-hover:bg-[#339b77]"),
    description: "Model Portfolios and Thematic Baskets",
    link: `/tools/smartkarma-model-portofolios`,
    type: "screeners",
    access: [...allUsers.filter((user) => user !== "isCorporateOfficer")],
    public: true,
    hideOnRestricted: true,
  },
  {
    id: "themes-explorer",
    title: "Themes Explorer",
    icon: <FontAwesomeIcon icon={faCubes} />,
    className: twMerge(screenerBg, "group-hover:bg-[#339b77]"),
    description:
      "Track 2,000+ investment themes, follow what matters to you, and instantly summarise latest Insight briefs",
    link: `/tools/themes-explorer`,
    enabled: true,
    access: [...allUsers],
    type: "screeners",
    public: true,
    isNew: true,
  },
  {
    id: "marketcast",
    title: "Private Company Marketcast",
    icon: <FontAwesomeIcon icon={faLayerGroup} />,
    className: twMerge(screenerBg, "group-hover:bg-[#339b77]"),
    description:
      "Track fundraising trends and identify emerging companies through US SEC Form D analysis",
    href: `https://marketcast.smartkarma.com`,
    enabled: true,
    access: [...allUsers.filter((user) => user !== "isPreviewPass")],
    type: "screeners",
    paidOnly: true,
    activityKey: "tool.view",
  },
  {
    id: "smartscore-momentum",
    title: "SmartScore Momentum",
    icon: <TbChartScatter />,
    className: twMerge(screenerBg, "group-hover:bg-[#339b77]"),
    description:
      "Track change in Price Momentum versus Change in Smartkarma SmartScore Momentum",
    link: `/tools/smartscore-momentum`,
    enabled: true,
    access: [...allUsers],
    type: "screeners",
    public: true,
    isNew: true,
  },
  {
    id: "comparables",
    title: "Comparables",
    icon: (
      <Image src={comparablesIcon} alt="Comparables" width={20} height={20} />
    ),
    className: "bg-smart-teal-300 group-hover:bg-[#339b77]",
    description: "Publicly Traded Comparables and Precedent Transactions",
    link: `/tools/comparables`,
    type: "screeners",
    public: true,
    access: [...allUsers],
    isNew: true,
  },
  {
    id: "kr-value-up",
    title: TOOL_KRVALUE_NAME,
    icon: (
      <Image
        src={require("../assets/revenue-alt-white.svg")}
        alt={TOOL_KRVALUE_NAME}
        width={25}
        height={25}
      />
    ),
    className: twMerge(screenerBg, "group-hover:bg-[#339b77]"),
    description: TOOL_KRVALUE_DESCRIPTION,
    link: `/tools/kr-value-up`,
    type: "screeners",
    access: [...allUsers],
    public: true,
    isNew: true,
  },
  {
    id: "shareholder-registry",
    title: TOOL_SHAREHOLDER_REGISTRY_NAME,
    icon: <FontAwesomeIcon icon={faUserTie} />,
    className: twMerge(screenerBg, "group-hover:bg-[#339b77]"),
    description: TOOL_SHAREHOLDER_REGISTRY_DESCRIPTION,
    link: `/tools/shareholder-registry`,
    type: "screeners",
    access: ["isInternalUser"],
    hideOnRestricted: true,
    isNew: true,
    isInternalOnly: true,
  },
  {
    id: "alt-data-analysis",
    title: TOOL_Alt_DATA_ANALYSIS_NAME,
    icon: <FontAwesomeIcon icon={faSearchPlus} />,
    className: twMerge(screenerBg, "group-hover:bg-[#339b77]"),
    description: TOOL_Alt_DATA_ANALYSIS_DESCRIPTION,
    link: `/tools/alt-data-analysis`,
    type: "screeners",
    access: allUsers,
    hideOnRestricted: true,
    isNew: true,
  },

  // Insight Providers
  {
    id: "payment-analytics",
    title: "Payment Analytics",
    icon: <FontAwesomeIcon icon={faChartBar} />,
    className: twMerge(insightProviderBg, "group-hover:bg-[#0266a5]"),
    description: "Track and measure your performance on Smartkarma",
    href: `${emberAppBaseUrl}/tools/payment-analytics`,
    queryParams: {
      tab: "overview",
      id: null,
    },
    enabled: true,
    permission: "canViewPaymentAnalytics",
    type: "insight-providers",
    access: ["canViewPaymentAnalytics"],
  },
  {
    id: "regulatory-process-and-conflicts-questionnaire",
    title: "Regulatory, Process and Conflicts Questionnaire",
    icon: <FontAwesomeIcon icon={faUniversity} />,
    className: twMerge(insightProviderBg, "group-hover:bg-[#0266a5]"),
    description:
      "Help us understand your current regulatory status and research process",
    href: `/tools/regulatory-process-and-conflicts-questionnaire`,
    enabled: true,
    withBrowserNavigation: true,
    permission: "canAccessRegulatoryForm",
    hideOnRestricted: true,
    type: "insight-providers",
    access: ["canAccessRegulatoryForm"],
  },

  // Channel Partner
  {
    id: "elysium",
    title: "Elysium",
    icon: <FontAwesomeIcon icon={faUniversalAccess} />,
    className: "bg-glacial-blue group-hover:bg-[#03bbd7]",
    isBeta: true,
    description: "Understand Client consumption, receive Analytics on the go",
    href: `${emberAppBaseUrl}/tools/elysium`,
    enabled: true,
    permission: "canViewElysium",
    type: "channel-partner",
    access: ["canViewElysium"],
  },

  // Commodities
  {
    id: "helixtap",
    title: "Rubber (Helixtap)",
    icon: (
      <Image
        alt="Helixtap"
        src={require("../assets/helixtap.svg")}
        width={20}
        height={30}
      />
    ),
    className: twMerge(commoditiesBg, "group-hover:bg-[#339b77]"),
    description: "Data, analysis, and forecasts for the Rubber market",
    link: "/helixtap",
    enabled: true,
    type: "commodities",
    access: [...allUsers.filter((user) => user !== "isCorporateOfficer")],
    hideOnRestricted: true,
  },
  {
    id: "precious-metal",
    title: TOOL_PRECIOUS_METALS_NAME,
    icon: <FontAwesomeIcon icon={faCoins} />,
    className: twMerge(commoditiesBg, "group-hover:bg-[#339b77]"),
    description: TOOL_PRECIOUS_METALS_DESCRIPTION,
    link: "/tools/precious-metal",
    enabled: true,
    type: "commodities",
    permission: "canViewPreciousMetalTool",
    access: ["canViewPreciousMetalTool"],
    paidOnly: true,
    isNew: true,
    hideOnRestricted: true,
  },

  {
    id: "chatbot",
    title: "IR Chatbot Builder",
    icon: <FontAwesomeIcon icon={faRobot} />,
    className: "bg-smart-teal-300 group-hover:bg-[#339b77]",
    description: "Convert Visitors to Shareholders",
    link: "/chatbot",
    enabled: true,
    type: "ir-tools",
    hideOnRestricted: true,
    access: ["isCorporateOfficer"],
  },
  {
    id: "ir-page-builder",
    title: "IR Website Builder",
    icon: <FontAwesomeIcon icon={faAtom} />,
    className: "bg-smart-teal-300 group-hover:bg-[#339b77]",
    description: "Build customized IR Website for your company",
    link: "/tools/builder",
    enabled: true,
    type: "ir-tools",
    hideOnRestricted: true,
    access: ["isCorporateOfficer"],
    isNew: true,
  },
];

export type Tool = (typeof tools)[number];
